import type {
    MouseEventHandler,
    PropsWithChildren,
    Ref,
    KeyboardEventHandler,
} from "react";
import React, { useImperativeHandle, useRef, forwardRef } from "react";

import cn from "classnames";

import type { BaseStates, PropsWithTsId } from "../../../types/react";

import classes from "./ButtonDiv.styl";

const BUTTON_DIV_TEST_ID = "button-div-test-id";

export type DivButtonProps = Pick<
    React.HTMLAttributes<HTMLDivElement>,
    | "className"
    | "role"
    | "tabIndex"
    | "onMouseEnter"
    | "onMouseLeave"
    | "onMouseUp"
    | "onMouseDown"
    | "onClick"
    | "onKeyDown"
    | "aria-label"
    | "aria-checked"
> & {
    state?: BaseStates | "loading" | "dragged";
    style?: React.CSSProperties;
} & PropsWithChildren &
    Partial<PropsWithTsId>;

export const ButtonDiv = forwardRef<HTMLDivElement, DivButtonProps>(
    (
        {
            className,
            style,
            onClick,
            onKeyDown,
            children,
            tabIndex = 0,
            state = "enabled",
            "data-tsid": tsid,
            ...props
        },
        ref: Ref<HTMLDivElement | null>,
    ) => {
        const containerRef = useRef<HTMLDivElement>(null);
        const isDisabled = state === "loading" || state === "disabled";

        const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = (event) => {
            if (isDisabled) {
                return;
            }

            onKeyDown?.(event);
            if (["Enter", "Space"].includes(event.code)) {
                event.preventDefault(); // Предотвращает прокрутку при нажатии пробела
                containerRef.current?.click();
            }
        };

        const handleClick: MouseEventHandler<HTMLDivElement> = (event) => {
            if (isDisabled) {
                return;
            }
            onClick?.(event);
        };

        const buttonDivClasses = cn(
            classes["container"],
            state !== "enabled" && classes[`__state-${state}`],
        );

        const getTabIndex = () => {
            if (isDisabled) {
                return -1;
            }

            return tabIndex;
        };

        useImperativeHandle(ref, () => containerRef.current);

        return (
            <div
                className={buttonDivClasses}
                style={style}
                data-tsid={BUTTON_DIV_TEST_ID}
            >
                <div
                    className={cn(classes["content"], className)}
                    role="button"
                    aria-disabled={isDisabled}
                    {...props}
                    tabIndex={getTabIndex()}
                    onClick={handleClick}
                    onKeyDown={handleKeyDown}
                    data-tsid={tsid}
                    ref={containerRef}
                >
                    {children}
                </div>
            </div>
        );
    },
);

ButtonDiv.displayName = "ButtonDiv";
